/**
 * TSX 모드 페이지 렌더러. 1920×1080 컨테이너 안의 iframe sandbox 에서 사용자 React 컴포넌트 실행.
 *
 * 격리:
 *  - iframe sandbox="allow-scripts allow-same-origin" — cookie 자동 전송으로 자산 URL 접근 가능.
 *  - srcdoc 으로 inline HTML — 같은 origin 으로 인식되어 부모의 React 객체 직접 inject.
 *  - 사용자 코드는 부모 DOM 에 접근할 수 있으나, 슬라이드 작성자 본인 코드라 위협 낮음.
 *
 * 컴파일:
 *  - 메인 컨텍스트의 esbuild-wasm 로 tsx → js 변환 (compileTsx).
 *  - iframe 안에서 globalThis.__SLIDE_PAGE__ 로 컴포넌트 받아 React.createRoot().render().
 *  - design tokens 는 CSS 변수, useAsset 은 글로벌 함수로 inject.
 */
import { compileTsx } from '@/lib/slides/tsx-compiler';
import type { SlideDesign } from '@innermate/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';

interface Props {
  tsxSource: string;
  design: SlideDesign;
  /** assetId → URL 매핑. iframe 안 useAsset(id) 가 lookup. */
  assetUrlMap: Map<string, string>;
}

declare global {
  interface Window {
    __innermate_slide_react__?: typeof React;
    __innermate_slide_reactdom__?: typeof ReactDOMClient;
  }
}

const SLIDE_CANVAS_W = 1920;
const SLIDE_CANVAS_H = 1080;

function buildSrcdoc(designVarsCss: string): string {
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<style>
html, body { margin: 0; padding: 0; width: ${SLIDE_CANVAS_W}px; height: ${SLIDE_CANVAS_H}px; overflow: hidden; }
#root { width: 100%; height: 100%; ${designVarsCss} }
.innermate-slide-error {
  position: absolute; inset: 0; padding: 80px;
  background: #fff5f5; color: #b00020;
  font-family: ui-monospace, "JetBrains Mono", monospace; font-size: 28px; line-height: 1.4;
  white-space: pre-wrap; overflow: auto;
}
</style>
</head>
<body>
<div id="root"></div>
<script>
window.__innermate_render__ = function(payload) {
  try {
    var React = parent.__innermate_slide_react__;
    var ReactDOM = parent.__innermate_slide_reactdom__;
    if (!React || !ReactDOM) {
      document.getElementById('root').innerHTML =
        '<div class="innermate-slide-error">React 객체 주입 실패. 페이지를 새로고침해 주세요.</div>';
      return;
    }
    // 글로벌 노출 — 사용자 코드 (jsxFactory=React.createElement) 와 ReactDOM.createRoot,
    // useAsset 헬퍼. appendChild 된 <script> 는 iframe 의 글로벌 스코프에서 실행되어
    // __innermate_render__ 클로저의 로컬 var 에 접근 불가 — 명시적으로 window 에 올려야 한다.
    window.React = React;
    window.ReactDOM = ReactDOM;
    window.useAsset = function(assetId) {
      var url = (payload.assetUrlMap || {})[assetId];
      return url || '';
    };
    // assetUrlMap 도 직접 노출 (사용자가 더 깊은 manipulation 원할 때).
    window.__assets__ = payload.assetUrlMap || {};

    // 디자인 토큰 CSS 변수 갱신.
    var rootEl = document.getElementById('root');
    rootEl.setAttribute('style', payload.designVarsCss || '');

    // 컴파일된 코드 실행.
    var script = document.createElement('script');
    script.textContent = payload.code +
      '\\n;try{var __Page__=globalThis.__SLIDE_PAGE__;if(__Page__){' +
      'var __root=window.__innermate_root__;' +
      'if(!__root){__root=window.__innermate_root__=ReactDOM.createRoot(document.getElementById("root"));}' +
      '__root.render(React.createElement(__Page__));' +
      '}else{document.getElementById("root").innerHTML=' +
      '"<div class=\\\\"innermate-slide-error\\\\">export default 함수가 없습니다. \`export default function Page() { ... }\` 형태로 작성해주세요.</div>";' +
      '}}catch(e){document.getElementById("root").innerHTML=' +
      '"<div class=\\\\"innermate-slide-error\\\\">런타임 에러: "+(e&&e.message?e.message:String(e))+"</div>";}';
    document.body.appendChild(script);
  } catch (e) {
    document.getElementById('root').innerHTML =
      '<div class="innermate-slide-error">예기치 못한 에러: ' + (e && e.message ? e.message : String(e)) + '</div>';
  }
};
window.__innermate_render_error__ = function(msg) {
  document.getElementById('root').innerHTML =
    '<div class="innermate-slide-error">컴파일 에러:\\n' + msg + '</div>';
};
window.parent.postMessage({ kind: 'innermate-slide-iframe-ready' }, '*');
</script>
</body>
</html>`;
}

export function TsxRenderer({ tsxSource, design, assetUrlMap }: Props) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = useState(false);
  const [compileError, setCompileError] = useState<string | null>(null);

  // 부모 window 에 React 객체 1회 노출 — iframe 이 parent.__innermate_slide_react__ 로 받음.
  useEffect(() => {
    window.__innermate_slide_react__ = React;
    window.__innermate_slide_reactdom__ = ReactDOMClient;
  }, []);

  const designVarsCss = useMemo(() => {
    return [
      `--osd-bg:${design.palette.bg}`,
      `--osd-text:${design.palette.text}`,
      `--osd-accent:${design.palette.accent}`,
      `--osd-font-display:${design.fonts.display}`,
      `--osd-font-body:${design.fonts.body}`,
      `--osd-size-hero:${design.typeScale.hero}px`,
      `--osd-size-body:${design.typeScale.body}px`,
      `--osd-radius:${design.radius}px`,
      `background:${design.palette.bg}`,
      `color:${design.palette.text}`,
      `font-family:${design.fonts.body}`,
    ].join(';');
  }, [design]);

  // iframe ready 시그널 받기.
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data && e.data.kind === 'innermate-slide-iframe-ready') {
        setReady(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // tsxSource 또는 design / assetUrlMap 변경 시 컴파일 + iframe 으로 전달.
  useEffect(() => {
    if (!ready) return;
    const iframeWin = iframeRef.current?.contentWindow as
      | (Window & {
          __innermate_render__?: (payload: {
            code: string;
            designVarsCss: string;
            assetUrlMap: Record<string, string>;
          }) => void;
          __innermate_render_error__?: (msg: string) => void;
        })
      | undefined;
    if (!iframeWin) return;

    let canceled = false;
    (async () => {
      const result = await compileTsx(tsxSource);
      if (canceled) return;
      if (!result.ok) {
        setCompileError(result.error);
        iframeWin.__innermate_render_error__?.(result.error);
        return;
      }
      setCompileError(null);
      const map: Record<string, string> = {};
      for (const [k, v] of assetUrlMap) map[k] = v;
      iframeWin.__innermate_render__?.({
        code: result.code,
        designVarsCss,
        assetUrlMap: map,
      });
    })();
    return () => {
      canceled = true;
    };
  }, [tsxSource, designVarsCss, assetUrlMap, ready]);

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <iframe
        ref={iframeRef}
        title="TSX slide preview"
        sandbox="allow-scripts allow-same-origin"
        srcDoc={buildSrcdoc(designVarsCss)}
        style={{ width: '100%', height: '100%', border: 0, background: design.palette.bg }}
      />
      {compileError && (
        <div
          style={{
            position: 'absolute',
            left: 24,
            bottom: 24,
            padding: '12px 16px',
            background: 'rgba(176,0,32,0.9)',
            color: 'white',
            fontFamily: 'ui-monospace, monospace',
            fontSize: 16,
            borderRadius: 8,
            maxWidth: 'calc(100% - 48px)',
            whiteSpace: 'pre-wrap',
          }}
        >
          컴파일 오류: {compileError}
        </div>
      )}
    </div>
  );
}
